import { Box, Stack, Text, useColorModeValue, Image, Icon, Tooltip, useBreakpointValue, Spinner, Center } from "@chakra-ui/react";
import React from "react";
import { FiDownload } from "react-icons/fi";
import { TextOverflowTooltip } from "screens/landing/components/TextOverflowTooltip";
import type { Purchase } from "types/billing/UserPurchasesResponse";

interface PurchaseItemProps {
  purchase: Purchase;
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "Pending";
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export const PurchaseItem: React.FC<PurchaseItemProps> = ({ purchase }) => {
  const buttonColor = useColorModeValue("primary.default", "primary.default");
  const tagColorValue = useColorModeValue("600", "600");
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });

  return (
    <Box
      p="1rem"
      _hover={{ bg: useColorModeValue("gray.50", "gray.800") }}
      onClick={() => purchase.downloadUrl && window.open(purchase.downloadUrl, "_blank")}>
      <Stack direction={isMobile ? "column" : "row"} justify="space-between" align={isMobile ? "start" : "center"} spacing={2}>
        <Stack direction="column" spacing={1}>
          {purchase.objectLogoUrl && (
            <Box width="16rem" alignContent={"center"}>
              <Image src={purchase.objectLogoUrl} alt="Company logo" height="20px" objectFit="contain" />
            </Box>
          )}
          <TextOverflowTooltip
            label={purchase.objectName || ""}
            style={{ fontWeight: 500 }}
            fontSize={purchase.objectLogoUrl ? "11px" : "sm"}
          />
        </Stack>
        <Stack direction="row" spacing={"1.5rem"} align="center">
          <Stack direction="column" spacing={1} align="end">
            <Stack direction="column" spacing={2} align="center">
              {purchase.downloadUrl && purchase.paymentDate && purchase.objectType ? (
                <Box
                  fontSize={"11px"}
                  borderRadius={"md"}
                  fontWeight={"bold"}
                  color={purchase.objectType === "investment" ? `green.${tagColorValue}` : `blue.${tagColorValue}`}>
                  {purchase.objectType.toUpperCase()}
                </Box>
              ) : (
                <Stack direction="row" spacing={1} align="center">
                  <Box
                    fontSize={"11px"}
                    borderRadius={"md"}
                    fontWeight={"bold"}
                    color={
                      !purchase.downloadUrl && purchase.paymentDate
                        ? `yellow.${tagColorValue}`
                        : purchase.paymentDate
                        ? `teal.${tagColorValue}`
                        : `orange.${tagColorValue}`
                    }>
                    {!purchase.downloadUrl && purchase.paymentDate ? "PROCESSING" : purchase.paymentDate ? "COMPLETED" : "CANCELLED"}
                  </Box>
                </Stack>
              )}
            </Stack>
            <Text fontSize="11px" color="gray.500">
              {purchase.paymentDate
                ? `Purchased on ${formatDate(purchase.paymentDate)}`
                : `Created on ${formatDate(purchase.creationDate)}`}
            </Text>
          </Stack>
          <Tooltip label={purchase.downloadUrl ? `Download ${purchase.objectType} report` : ""} placement="top">
            <Box>
              {!purchase.downloadUrl && purchase.paymentDate ? (
                <Center width={"22.4px"}>
                  <Spinner
                    color={`yellow.${tagColorValue}`}
                    emptyColor="gray.200"
                    size="sm"
                    boxSize={"1.2rem"}
                    speed="0.65s"
                    thickness="3px"
                  />
                </Center>
              ) : (
                <Icon
                  onClick={(e) => {
                    e.preventDefault();
                    purchase.downloadUrl && window.open(purchase.downloadUrl, "_blank");
                  }}
                  _hover={{}}
                  _active={{}}
                  aria-label="Download report"
                  as={FiDownload}
                  opacity={purchase.downloadUrl ? 1 : 0}
                  color={buttonColor}
                  fontSize={"1.4rem"}
                />
              )}
            </Box>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};
